// NovahGenerateChat — chat conversacional de generación de contenido (Novah).
// Orquestador: mantiene el estado del flujo (steps + datos recolectados) y los
// handlers; delega el render de cada step a los widgets de ./novahGenerate/widgets
// y usa los styled de ./novahGenerate/styled + tipos/constantes de ./novahGenerate/types.
import { useState, useRef, useCallback, useEffect } from 'react';
import { SocialNetwork, PlatformPostCounts, KIE_MODELS, PreviewFormat, TopicRequest } from '@agency-smm/shared';
import { ExportQuality } from '../utils/canvasRenderer';
import { apiClient } from '../services/apiClient';
import {
  CONTACT_STYLE_ID,
  BakedStyleOption,
  ContactDraft,
  ChatMsg,
  Step,
  ImageStyle,
  PLATFORMS,
  FORMAT_OPTIONS,
  LANGUAGE_OPTIONS,
  GEN_STATUS_MESSAGES,
  suggestFormat,
} from './novahGenerate/types';
import {
  ChatContainer, MsgRow, Avatar, Bubble, TypingDots, WidgetWrap,
  ProgressBar, ProgressFill, BackBtn, SkipBtn,
} from './novahGenerate/styled';
import {
  PlatformPicker, CountPicker, FormatPicker, TopicsStep, LanguagePicker,
  ImageStylePicker, BakedStylePicker, TransferPicker, PhotoPicker, ContactStep, AiModelPicker, ConfirmSummary,
} from './novahGenerate/widgets';

// Re-export para los consumidores que lo importaban desde acá (ej. GeneratePage).
export type { UserPlan } from './novahGenerate/types';
import type { UserPlan } from './novahGenerate/types';
import { useTopicsDraft, TopicDraft } from './novahGenerate/useTopicsDraft';

// ─── Props ──────────────────────────────────────────────────────────────────

interface NovahGenerateChatProps {
  workspaceId: string;
  workspaceName: string;
  credits: number;
  hasBackgroundImages: boolean;
  userPlan: UserPlan;
  // ¿Mostrar el picker de estilo de imagen? true para admins/mods, o para
  // usuarios normales cuando el admin puso la política en "They choose". Si es
  // false, se auto-asigna server-side y salteamos el paso.
  canPickStyle: boolean;
  onNavigateBilling: () => void;
  onGenerate: (config: {
    platformCounts: PlatformPostCounts;
    customPrompt: string;
    topics?: TopicRequest[];
    imageStyle: ImageStyle;
    previewFormat: PreviewFormat;
    kieModel?: string;
    language?: string;
    styleIds?: string[];
    useLogo?: boolean;
    autoFillGreenbox?: boolean;
    photoIds?: string[];
    aiTransferReferenceUrl?: string;
    aiTransferVaryLayout?: boolean;
    aiTransferInstructions?: string;
  }) => void;
  isStreaming: boolean;
  streamProgress: { current: number; total: number };
  onExportCSV: (quality: ExportQuality) => void;
  onDownloadZIP: (quality: ExportQuality) => void;
  postsCount: number;
  // Se dispara cuando la tanda termina (paso 'done'). La página lo usa para
  // ocultar el chat y mostrar las generaciones.
  onDone?: () => void;
}

// ─── Component ──────────────────────────────────────────────────────────────

export function NovahGenerateChat({
  workspaceId,
  workspaceName,
  hasBackgroundImages,
  userPlan,
  canPickStyle,
  onNavigateBilling,
  onGenerate,
  isStreaming,
  streamProgress,
  postsCount,
  onDone,
}: NovahGenerateChatProps) {
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [typing, setTyping] = useState(false);
  const [step, setStep] = useState<Step>('platforms');
  const [stepHistory, setStepHistory] = useState<Step[]>([]);
  const msgIdRef = useRef(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const hasStarted = useRef(false);

  // Collected data
  const [selectedPlatforms, setSelectedPlatforms] = useState<Set<SocialNetwork>>(new Set());
  const [countPerPlatform, setCountPerPlatform] = useState(2);
  // Multi-tema: la lista, la caja en curso y la aritmetica viven en el hook
  // (este archivo ya pasa el tope de 500 lineas del repo).
  const topics = useTopicsDraft(workspaceId, countPerPlatform, selectedPlatforms.size);
  // Default 'stock': estilo más directo y confiable (fotos stock), evita el extra del modelo IA.
  const [imageStyle, setImageStyle] = useState<ImageStyle>('stock');
  const [previewFormat, setPreviewFormat] = useState<PreviewFormat>('square');
  const [language, setLanguage] = useState<string>('Spanish');
  const [kieModel, setKieModel] = useState<string | undefined>(undefined);

  // AI-styled posters
  const [bakedStyles, setBakedStyles] = useState<BakedStyleOption[]>([]);
  const [selectedStyleIds, setSelectedStyleIds] = useState<string[]>([]);
  const [useLogo, setUseLogo] = useState(false);
  const [autoFillGreenbox, setAutoFillGreenbox] = useState(false);
  const [myPhotos, setMyPhotos] = useState<{ id: string; url: string; name?: string }[]>([]);
  const [myAds, setMyAds] = useState<{ id: string; url: string; label: string }[]>([]);
  const [selectedPhotoIds, setSelectedPhotoIds] = useState<string[]>([]);
  // "Dejar la foto intacta" dentro del flujo AI-styled: no transforma la foto,
  // la deja tal cual como fondo y compone el template de texto/logo encima
  // (rutea al camino 'asset'). Ticket 72968892.
  const [keepPhotoIntact, setKeepPhotoIntact] = useState(false);
  // AI Transfer: referencia elegida (portada de estilo o subida) + estado de subida.
  const [transferRefUrl, setTransferRefUrl] = useState('');
  // AI Transfer · "Vary layout". ON por defecto: con la misma referencia y el
  // mismo prompt, pedir 10 anuncios devolvía 10 casi idénticos.
  const [transferVaryLayout, setTransferVaryLayout] = useState(true);
  // Indicaciones libres del AI Transfer ("agregá sólo mi marca al producto").
  // Van SOLO al prompt de la imagen; el customPrompt del chat sigue gobernando
  // el texto del post, que es otra cosa.
  const [transferInstructions, setTransferInstructions] = useState('');
  const [refUploading, setRefUploading] = useState(false);
  // Brand info completo (para mergear contacto sin pisar el resto al guardar).
  const [brandInfoFull, setBrandInfoFull] = useState<Record<string, unknown> | null>(null);
  const [contactDraft, setContactDraft] = useState<ContactDraft>({
    address: '', phoneNumber: '', businessHours: '', whatsapp: '', email: '',
  });
  const [savingContact, setSavingContact] = useState(false);

  // Cargar estilos baked + brand info una vez (para el selector y el contacto).
  useEffect(() => {
    // El workspace va en la query porque el orden del picker es por uso: los
    // estilos que ESTA marca usa salen primero.
    apiClient.get<{ styles: BakedStyleOption[] }>(`/api/quickgen/styles?workspaceId=${encodeURIComponent(workspaceId)}`)
      .then((d) => setBakedStyles(d.styles))
      .catch(() => { /* selector queda vacío si falla */ });
    apiClient.get<Record<string, unknown>>(`/api/workspaces/${workspaceId}/brand-info`)
      .then((bi) => {
        if (!bi) return;
        setBrandInfoFull(bi);
        setContactDraft({
          address: (bi.address as string) || '',
          phoneNumber: (bi.phoneNumber as string) || '',
          businessHours: (bi.businessHours as string) || '',
          whatsapp: (bi.whatsapp as string) || '',
          email: (bi.email as string) || '',
        });
      })
      .catch(() => { /* sin brand info, los campos quedan vacíos */ });
    // MIS ADS — los posts ya generados sirven como referencia de AI Transfer:
    // son diseños propios y aprobados, mejores que cualquier catálogo. Solo los
    // que tienen imagen.
    apiClient.get<{ id: string; imageUrl: string | null; network?: string; createdAt?: string }[]>(
      `/api/workspaces/${workspaceId}/posts?limit=40`)
      .then((ps) => setMyAds(
        (Array.isArray(ps) ? ps : [])
          .filter((p) => !!p.imageUrl)
          .map((p) => ({ id: p.id, url: p.imageUrl as string, label: p.network || 'Post' })),
      ))
      .catch(() => { /* sin ads previos, solo queda el catálogo */ });
    // MY PHOTOS — para "usar mis fotos como base" en los AI-styled posters.
    apiClient.get<{ id: string; url: string; name?: string }[]>(`/api/workspaces/${workspaceId}/background-images`)
      .then((imgs) => setMyPhotos(Array.isArray(imgs) ? imgs : []))
      .catch(() => { /* sin fotos, el picker no aparece */ });
  }, [workspaceId]);

  const togglePlatform = (id: SocialNetwork) => {
    setSelectedPlatforms((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const togglePhoto = (id: string) => {
    setSelectedPhotoIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };
  // AI Transfer, multi-selección: el server ya rota las fotos post por post (round-robin en
  // routes/generate.ts) y recibe un array. Reemplazar en vez de acumular era lo
  // ÚNICO que ataba AI Transfer a una sola foto: generar 5 posts daba 5 veces la
  // misma imagen.
  const selectTransferPhoto = (id: string) =>
    setSelectedPhotoIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  // Subir una foto propia SIN salir del flujo: antes había que irse a
  // Brand → Assets y volver. Se agrega a la lista y queda seleccionada.
  const [photoUploading, setPhotoUploading] = useState(false);
  const uploadPhoto = async (file: File) => {
    setPhotoUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const img = await apiClient.upload<{ id: string; url: string; name?: string }>(
        `/api/workspaces/${workspaceId}/background-images/upload`, fd,
      );
      if (img?.id && img?.url) {
        setMyPhotos((prev) => [{ id: img.id, url: img.url, name: img.name }, ...prev]);
        setSelectedPhotoIds((prev) => (prev.includes(img.id) ? prev : [...prev, img.id]));
      }
    } catch { /* si falla, el user puede elegir de las que ya tiene */ }
    finally { setPhotoUploading(false); }
  };

  // Subir una referencia propia → background-images (R2) → setear como referencia.
  const uploadReference = async (file: File) => {
    setRefUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const img = await apiClient.upload<{ url: string }>(`/api/workspaces/${workspaceId}/background-images/upload`, fd);
      if (img?.url) setTransferRefUrl(img.url);
    } catch { /* si falla, el user puede elegir del catálogo */ }
    finally { setRefUploading(false); }
  };

  const [genStatusIdx, setGenStatusIdx] = useState(0);

  useEffect(() => {
    if (step !== 'generating') {
      setGenStatusIdx(0);
      return;
    }
    const interval = setInterval(() => {
      setGenStatusIdx(prev => (prev + 1) % GEN_STATUS_MESSAGES.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [step]);

  const pushStep = (next: Step) => {
    setStepHistory(prev => [...prev, step]);
    setStep(next);
  };

  const goBack = () => {
    if (stepHistory.length === 0) return;
    const prev = stepHistory[stepHistory.length - 1];
    setStepHistory(h => h.slice(0, -1));
    setStep(prev);
  };

  const nextId = () => ++msgIdRef.current;
  const scroll = useCallback(() => {
    setTimeout(() => scrollRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }), 80);
  }, []);

  const addNovah = useCallback((text: string, widget?: string): Promise<void> => {
    return new Promise((resolve) => {
      setTyping(true);
      scroll();
      setTimeout(() => {
        setTyping(false);
        setMessages((prev) => [...prev, { id: nextId(), from: 'novah', text, widget }]);
        scroll();
        resolve();
      }, 500);
    });
  }, [scroll]);

  const addUser = useCallback((text: string) => {
    setMessages((prev) => [...prev, { id: nextId(), from: 'user', text }]);
    scroll();
  }, [scroll]);

  // Intro
  useEffect(() => {
    if (hasStarted.current) return;
    hasStarted.current = true;
    const run = async () => {
      await new Promise((r) => setTimeout(r, 300));
      await addNovah(`Ready to create content for **${workspaceName}**? Pick your platforms.`, 'platforms');
    };
    run();
  }, [addNovah, workspaceName]);

  // Transition to done when streaming finishes with posts
  useEffect(() => {
    if (step === 'generating' && !isStreaming && postsCount > 0) {
      addNovah(`Done! ${postsCount} posts ready. Tap any post to preview or download.`, 'done');
      setStep('done');
      onDone?.();
    }
  }, [isStreaming, postsCount, step, addNovah, onDone]);

  // ─── Step Handlers ──────────────────────────────────────────────────────

  const confirmPlatforms = async () => {
    if (selectedPlatforms.size === 0) return;
    const names = Array.from(selectedPlatforms).map((p) => PLATFORMS.find((x) => x.id === p)?.label).join(', ');
    addUser(names);
    // Sugerir el formato según las redes elegidas (TikTok→vertical, YouTube→
    // widescreen, etc.) para evitar el clásico "generé vertical para el Feed".
    setPreviewFormat(suggestFormat(Array.from(selectedPlatforms)));
    await addNovah('How many posts per platform?', 'count');
    pushStep('count');
  };

  const confirmCount = async (n: number) => {
    setCountPerPlatform(n);
    const total = selectedPlatforms.size * n;
    addUser(`${n} per platform (${total} total)`);
    await addNovah('What format should the posts be?', 'format');
    pushStep('format');
  };

  const confirmFormat = async (fmt: PreviewFormat) => {
    setPreviewFormat(fmt);
    const opt = FORMAT_OPTIONS.find(o => o.id === fmt);
    addUser(opt ? `${opt.icon} ${opt.label}` : fmt);
    await addNovah('What should the posts be about? Add one topic at a time — each gets its own number of posts. Or let me surprise you.', 'prompt');
    pushStep('prompt');
  };

  const confirmTopics = async (committed: TopicDraft[]) => {
    addUser(committed.length === 0
      ? 'Surprise me'
      : committed.map((t) => `${t.topic} (${t.count})`).join(' · '));
    await addNovah('In which language should I write?', 'language');
    pushStep('language');
  };

  const confirmLanguage = async (lang: string) => {
    setLanguage(lang);
    const opt = LANGUAGE_OPTIONS.find(o => o.id === lang);
    addUser(opt ? `${opt.flag} ${opt.label}` : lang);
    // Si no puede elegir (auto-asignación server-side), salteamos el picker y
    // vamos directo al resumen.
    if (!canPickStyle) {
      await addNovah("Here's the plan:", 'confirm');
      pushStep('confirm');
      return;
    }
    await addNovah('What kind of images should I use?', 'style');
    pushStep('style');
  };

  const confirmStyle = async (style: ImageStyle) => {
    setImageStyle(style);
    const labels: Record<ImageStyle, string> = {
      stock: 'Stock photos',
      pastel: 'Solid colors',
      asset: 'My own photos',
      ai: 'AI Generated',
      mix: 'Mix (stock + solid)',
      styled: 'AI-styled posters',
      transfer: 'AI Transfer',
    };
    addUser(labels[style]);

    // AI-styled posters → elegir uno o varios estilos (poster full IA, round-robin).
    if (style === 'styled') {
      await addNovah('Pick one or more poster styles. Every post will come back as full AI artwork, cycling through your picks.', 'baked-style');
      pushStep('baked-style');
      return;
    }

    // AI Transfer → elegir referencia + foto + logo (la IA los combina).
    if (style === 'transfer') {
      await addNovah('Elegí un anuncio de referencia, tu foto y (opcional) tu logo. Los combino en un anuncio que copia el diseño de la referencia con TU foto real.', 'ai-transfer');
      pushStep('ai-transfer');
      return;
    }

    // My photos → dejar que el usuario ELIJA qué fotos usar (antes se tomaban al
    // azar de todo el workspace). Requiere ≥1 foto.
    if (style === 'asset' && myPhotos.length > 0) {
      await addNovah('Which of your photos should I use? Pick one or more.', 'photos');
      pushStep('photos');
      return;
    }

    // Business users choosing AI or Mix get to pick the AI model
    const needsModelPick = userPlan === 'business' && (style === 'ai' || style === 'mix');
    if (needsModelPick) {
      await addNovah('Which AI model should I use for your images?', 'ai-model');
      pushStep('ai-model');
    } else {
      await addNovah("Here's the plan:", 'confirm');
      pushStep('confirm');
    }
  };

  const toggleBakedStyle = (id: string) => {
    setSelectedStyleIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  // Continúa desde AI Transfer: referencia + foto (+ logo) elegidos.
  const confirmTransfer = async () => {
    if (!transferRefUrl) return;
    const hasPhoto = selectedPhotoIds.length > 0;
    addUser(`Referencia${hasPhoto ? ' + mi foto' : ' (sin foto)'}${useLogo ? ' + logo' : ''}`);
    await addNovah("Here's the plan:", 'confirm');
    pushStep('confirm');
  };

  // Continúa desde el selector de "My photos": el usuario eligió qué fotos usar.
  const confirmAssetPhotos = async () => {
    if (selectedPhotoIds.length === 0) return;
    addUser(`${selectedPhotoIds.length} photo${selectedPhotoIds.length !== 1 ? 's' : ''} selected`);
    await addNovah("Here's the plan:", 'confirm');
    pushStep('confirm');
  };

  // Continúa desde el selector de estilos. Si se eligió un estilo que muestra
  // contacto (bold-service-ad) y falta algún dato, Novah lo pide antes de generar.
  const confirmBakedStyles = async () => {
    // Con "foto intacta" no hacen falta estilos: alcanza con ≥1 foto elegida.
    const intactReady = keepPhotoIntact && selectedPhotoIds.length > 0;
    if (selectedStyleIds.length === 0 && !intactReady) return;
    if (intactReady) {
      addUser(`Keep my ${selectedPhotoIds.length} photo${selectedPhotoIds.length !== 1 ? 's' : ''} as-is + add text`);
      await addNovah("Here's the plan:", 'confirm');
      pushStep('confirm');
      return;
    }
    const names = selectedStyleIds
      .map((id) => bakedStyles.find((s) => s.id === id)?.label || id)
      .join(', ');
    addUser(names);

    const usesContact = selectedStyleIds.includes(CONTACT_STYLE_ID);
    const missingContact = !contactDraft.address || !contactDraft.phoneNumber;
    if (usesContact && missingContact) {
      await addNovah('That service-style poster shows contact info. Want to add or confirm it? I only use what you give me — I never make up phone numbers or hours. Leave blank to omit.', 'contact');
      pushStep('contact');
    } else {
      await addNovah("Here's the plan:", 'confirm');
      pushStep('confirm');
    }
  };

  // Guarda el contacto en brand_info MERGEANDO sobre el record completo. El PUT
  // de brand-info reemplaza TODAS las columnas, así que si no tenemos el record
  // completo (el fetch inicial pudo fallar) NO guardamos — sino borraríamos el
  // resto del brand profile. En ese caso seguimos sin guardar (los datos que el
  // user escribió igual no se pierden de la generación porque van por DB; pero
  // acá priorizamos no destruir el perfil).
  const saveContactAndContinue = async () => {
    setSavingContact(true);
    let base: Record<string, unknown> | null | undefined = brandInfoFull;
    if (!base) {
      // Reintentar el fetch para tener el record completo antes de pisar.
      try {
        base = await apiClient.get<Record<string, unknown>>(`/api/workspaces/${workspaceId}/brand-info`);
      } catch {
        base = undefined; // fetch falló: estado desconocido → no arriesgamos un wipe
      }
    }

    if (base === undefined) {
      // No pudimos confirmar el record: seguimos sin guardar para no destruir nada.
      setSavingContact(false);
      addUser('Contact details (not saved — could not load brand profile)');
      await addNovah("Here's the plan:", 'confirm');
      pushStep('confirm');
      return;
    }

    try {
      const merged = {
        ...(base || {}),
        address: contactDraft.address,
        phoneNumber: contactDraft.phoneNumber,
        businessHours: contactDraft.businessHours,
        whatsapp: contactDraft.whatsapp,
        email: contactDraft.email,
      };
      await apiClient.put(`/api/workspaces/${workspaceId}/brand-info`, merged);
      setBrandInfoFull(merged);
    } catch {
      /* guardado falló: el prompt omite lo que no esté en DB, sin inventar */
    } finally {
      setSavingContact(false);
    }
    addUser('Contact details saved');
    await addNovah("Here's the plan:", 'confirm');
    pushStep('confirm');
  };

  const skipContact = async () => {
    addUser('Skip contact');
    await addNovah("Here's the plan:", 'confirm');
    pushStep('confirm');
  };

  const confirmAiModel = async (model: string) => {
    setKieModel(model);
    const modelLabel = model === 'mix'
      ? 'Mix all models'
      : KIE_MODELS.find(m => m.id === model)?.name || model;
    addUser(modelLabel);
    await addNovah("Here's the plan:", 'confirm');
    pushStep('confirm');
  };

  const triggerGenerate = () => {
    const platformCounts: PlatformPostCounts = {};
    // Los temas mandan sobre el presupuesto del paso de cantidad: si los temas
    // piden 12 y ahi se eligieron 10, van 12. El server recalcula lo mismo por
    // si este cliente esta desactualizado.
    selectedPlatforms.forEach((p) => { platformCounts[p] = topics.effectivePerPlatform; });
    const total = topics.totalPosts;
    addUser(`Generate ${total} posts`);
    topics.persistLastUsed(topics.list);
    setStep('generating');
    // "Foto intacta" (dentro del flujo styled): la foto NO se transforma → ruteamos
    // al camino 'asset' (foto tal cual + template de texto/logo encima). Los estilos
    // baked y el greenbox no aplican; el logo sí (autoStampLogo vía useLogo).
    const intact = imageStyle === 'styled' && keepPhotoIntact && selectedPhotoIds.length > 0;
    const isTransfer = imageStyle === 'transfer';
    onGenerate({
      platformCounts,
      // customPrompt sigue viajando vacio: los callers viejos (QuickGen, panel
      // legacy) lo usan y el server lo respeta cuando no hay topics.
      customPrompt: '',
      topics: topics.toRequest(),
      imageStyle: intact ? 'asset' : imageStyle,
      previewFormat, kieModel, language,
      styleIds: (imageStyle === 'styled' && !intact) ? selectedStyleIds : [],
      useLogo: (imageStyle === 'styled' || isTransfer) ? useLogo : false,
      autoFillGreenbox: (imageStyle === 'styled' && !intact) ? autoFillGreenbox : false,
      // 'styled' usa las fotos como base i2i; 'asset'/'transfer' usan EXACTAMENTE
      // las fotos elegidas (sin random).
      photoIds: (imageStyle === 'styled' || imageStyle === 'asset' || isTransfer) ? selectedPhotoIds : [],
      // AI Transfer: la referencia elegida (portada de estilo o subida).
      aiTransferReferenceUrl: isTransfer ? transferRefUrl : undefined,
      aiTransferVaryLayout: isTransfer ? transferVaryLayout : undefined,
      aiTransferInstructions: isTransfer ? transferInstructions.trim() || undefined : undefined,
    });
  };

  const resetFlow = async () => {
    setSelectedPlatforms(new Set());
    setCountPerPlatform(2);
    topics.clear();
    setImageStyle('stock'); // reset al default directo (ver useState arriba)
    setSelectedStyleIds([]);
    setUseLogo(false);
    setSelectedPhotoIds([]);
    setKeepPhotoIntact(false);
    setTransferRefUrl('');
    setTransferVaryLayout(true);
    setTransferInstructions('');
    setPreviewFormat('square');
    setLanguage('Spanish');
    setMessages([]);
    setStep('platforms');
    setStepHistory([]);
    hasStarted.current = false;
    msgIdRef.current = 0;
    await new Promise((r) => setTimeout(r, 100));
    hasStarted.current = true;
    await addNovah(`Ready to create more content for **${workspaceName}**? Pick your platforms.`, 'platforms');
  };

  // ─── Total count helper ─────────────────────────────────────────────────
  // Los temas mandan: si piden mas que el paso de cantidad, sube el total.
  const totalPosts = topics.totalPosts;

  // ─── Render ─────────────────────────────────────────────────────────────

  return (
    <div>
      <ChatContainer>
        {messages.map((msg) => (
          <div key={msg.id}>
            <MsgRow $from={msg.from}>
              {msg.from === 'novah' && <Avatar><img src="/novah_laugh.png" alt="Novah" /></Avatar>}
              <Bubble $from={msg.from}>{msg.text.split('**').map((part, i) => i % 2 === 1 ? <strong key={i}>{part}</strong> : part)}</Bubble>
            </MsgRow>

            {/* Widgets render after their novah message */}
            {msg.from === 'novah' && msg.widget === 'platforms' && step === 'platforms' && (
              <PlatformPicker
                selectedPlatforms={selectedPlatforms}
                togglePlatform={togglePlatform}
                confirmPlatforms={confirmPlatforms}
              />
            )}

            {msg.from === 'novah' && msg.widget === 'count' && step === 'count' && (
              <CountPicker confirmCount={confirmCount} />
            )}

            {msg.from === 'novah' && msg.widget === 'prompt' && step === 'prompt' && (
              <TopicsStep topics={topics} budgetPerPlatform={countPerPlatform} confirmTopics={confirmTopics} />
            )}

            {msg.from === 'novah' && msg.widget === 'format' && step === 'format' && (
              <FormatPicker confirmFormat={confirmFormat} selectedPlatforms={selectedPlatforms} />
            )}

            {msg.from === 'novah' && msg.widget === 'language' && step === 'language' && (
              <LanguagePicker confirmLanguage={confirmLanguage} />
            )}

            {msg.from === 'novah' && msg.widget === 'style' && step === 'style' && (
              <ImageStylePicker
                userPlan={userPlan}
                hasBackgroundImages={hasBackgroundImages}
                confirmStyle={confirmStyle}
                onNavigateBilling={onNavigateBilling}
              />
            )}

            {msg.from === 'novah' && msg.widget === 'photos' && step === 'photos' && (
              <PhotoPicker
                myPhotos={myPhotos}
                selectedPhotoIds={selectedPhotoIds}
                togglePhoto={togglePhoto}
                confirmPhotos={confirmAssetPhotos}
                uploadPhoto={uploadPhoto}
                photoUploading={photoUploading}
                totalPosts={totalPosts}
              />
            )}

            {msg.from === 'novah' && msg.widget === 'baked-style' && step === 'baked-style' && (
              <BakedStylePicker
                bakedStyles={bakedStyles}
                selectedStyleIds={selectedStyleIds}
                toggleBakedStyle={toggleBakedStyle}
                useLogo={useLogo}
                setUseLogo={setUseLogo}
                myPhotos={myPhotos}
                selectedPhotoIds={selectedPhotoIds}
                togglePhoto={togglePhoto}
                confirmBakedStyles={confirmBakedStyles}
                autoFillGreenbox={autoFillGreenbox}
                setAutoFillGreenbox={setAutoFillGreenbox}
                anyGreenboxSelected={selectedStyleIds.some((id) => bakedStyles.find((s) => s.id === id)?.isGreenbox)}
                keepPhotoIntact={keepPhotoIntact}
                setKeepPhotoIntact={setKeepPhotoIntact}
              />
            )}

            {msg.from === 'novah' && msg.widget === 'ai-transfer' && step === 'ai-transfer' && (
              <TransferPicker
                bakedStyles={bakedStyles}
                transferRefUrl={transferRefUrl}
                selectReference={setTransferRefUrl}
                uploadReference={uploadReference}
                refUploading={refUploading}
                myPhotos={myPhotos}
                selectedPhotoIds={selectedPhotoIds}
                togglePhoto={selectTransferPhoto}
                useLogo={useLogo}
                setUseLogo={setUseLogo}
                hasLogo={true}
                confirmTransfer={confirmTransfer}
                totalPosts={totalPosts}
                uploadPhoto={uploadPhoto}
                photoUploading={photoUploading}
                myAds={myAds}
                varyLayout={transferVaryLayout}
                setVaryLayout={setTransferVaryLayout}
                instructions={transferInstructions}
                setInstructions={setTransferInstructions}
              />
            )}

            {msg.from === 'novah' && msg.widget === 'contact' && step === 'contact' && (
              <ContactStep
                contactDraft={contactDraft}
                setContactDraft={setContactDraft}
                savingContact={savingContact}
                saveContactAndContinue={saveContactAndContinue}
                skipContact={skipContact}
              />
            )}

            {msg.from === 'novah' && msg.widget === 'ai-model' && step === 'ai-model' && (
              <AiModelPicker confirmAiModel={confirmAiModel} />
            )}

            {msg.from === 'novah' && msg.widget === 'confirm' && step === 'confirm' && (
              <ConfirmSummary
                selectedPlatforms={selectedPlatforms}
                canPickStyle={canPickStyle}
                imageStyle={imageStyle}
                selectedStyleIds={selectedStyleIds}
                keepPhotoIntact={keepPhotoIntact}
                selectedPhotoCount={selectedPhotoIds.length}
                previewFormat={previewFormat}
                topics={topics.list}
                topicDemand={topics.demand}
                budgetPerPlatform={countPerPlatform}
                effectivePerPlatform={topics.effectivePerPlatform}
                totalPosts={totalPosts}
                triggerGenerate={triggerGenerate}
                resetFlow={resetFlow}
              />
            )}

            {msg.from === 'novah' && msg.widget === 'done' && step === 'done' && (
              <WidgetWrap>
                {/* Download / export moved to the per-post selection bar on
                    the Generate page — Novah's chat just confirms and offers
                    to keep generating. */}
                <SkipBtn onClick={resetFlow}>Generate more</SkipBtn>
              </WidgetWrap>
            )}
          </div>
        ))}

        {typing && (
          <MsgRow $from="novah">
            <Avatar><img src="/novah_laugh.png" alt="Novah" /></Avatar>
            <Bubble $from="novah"><TypingDots><span /><span /><span /></TypingDots></Bubble>
          </MsgRow>
        )}

        {step === 'generating' && (
          <WidgetWrap>
            <MsgRow $from="novah">
              <Avatar><img src="/novah_laugh.png" alt="Novah" /></Avatar>
              <Bubble $from="novah">
                <div style={{ fontSize: '13px', marginBottom: 6, opacity: 0.85, fontStyle: 'italic' }}>
                  {GEN_STATUS_MESSAGES[genStatusIdx]}
                </div>
                <div style={{ fontWeight: 600 }}>
                  {streamProgress.current}/{streamProgress.total} posts
                </div>
                <ProgressBar>
                  <ProgressFill $pct={streamProgress.total ? (streamProgress.current / streamProgress.total) * 100 : 0} />
                </ProgressBar>
              </Bubble>
            </MsgRow>
          </WidgetWrap>
        )}

        {stepHistory.length > 0 && !['generating', 'done'].includes(step) && (
          <BackBtn onClick={goBack}>&larr; Go back</BackBtn>
        )}
        <div ref={scrollRef} />
      </ChatContainer>
    </div>
  );
}
